import React, { useCallback, useEffect, useState } from 'react';
import useFetch from 'use-http';
import { Loader2, X } from 'src/components/common/icons';
import { ITableColumn, ITableConfig, Table } from 'src/components/common/table';
import PagedResultSet from 'src/models/common/PagedResultSet';
import { Field, SelectField } from './Master';

type LeaseSsrsReportType = 'transactions' | 'nonZeroBalance';

interface ILeaseSsrsReportDialogProps {
    isOpen: boolean;
    reportType: LeaseSsrsReportType;
    onClose: () => void;
    initialLeaseNo?: string;
    initialClientNo?: string;
    initialProjectNo?: string;
}

const NON_ZERO_SORT_OPTIONS = [
    { value: '1', label: 'Client/Project' },
    { value: '2', label: 'Lease Number' },
    { value: '3', label: 'Lease Date' },
    { value: '4', label: 'A/R Balance' },
    { value: '5', label: 'A/P Balance' },
];

const EXCEL_REPORT_FORMAT = 'EXCELOPENXML';
const PAGE_SIZE = 25;

interface ILeaseTransactionReportRow {
    id: string;
    leaseNo?: number;
    clNo?: number;
    prNo?: number;
    tranType?: string;
    tranNo?: string;
    tranDate?: string;
    dscr?: string;
    amount?: number;
    arCol?: number;
    apCol?: number;
}

interface ILeaseNonZeroBalanceReportRow {
    id: string;
    clNo?: number;
    prNo?: number;
    leaseNo?: number;
    leaseDate?: string;
    lessor?: string;
    arBalance?: number;
    apBalance?: number;
}

type LeaseReportRow = ILeaseTransactionReportRow | ILeaseNonZeroBalanceReportRow;

const formatDateToMMDDYYYY = (value: string): string => {
    if (!value) {
        return '';
    }

    const parts = value.split('-');
    if (parts.length !== 3) {
        return value;
    }

    return `${parts[1]}/${parts[2]}/${parts[0]}`;
};

const formatDateForDisplay = (value?: string): string => {
    if (!value) {
        return '-';
    }

    const parsed = new Date(value);
    if (isNaN(parsed.getTime())) {
        return '-';
    }

    return parsed.toLocaleDateString('en-US');
};

const formatCurrency = (value?: number | string): string => {
    if (value === null || value === undefined || value === '') {
        return '-';
    }

    const parsed = typeof value === 'number' ? value : parseFloat(value);
    if (isNaN(parsed)) {
        return '-';
    }

    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
    }).format(parsed);
};

const getColumnsForReport = (reportType: LeaseSsrsReportType): ITableColumn[] => {
    if (reportType === 'transactions') {
        return [
            { name: 'Lease No.', mapping: 'leaseNo' },
            { name: 'Client', mapping: 'clNo' },
            { name: 'Project', mapping: 'prNo' },
            { name: 'Transaction Type', mapping: 'tranType' },
            { name: 'Transaction Number', mapping: 'tranNo' },
            { name: 'Trans. Date', mapping: 'tranDate', format: formatDateForDisplay },
            { name: 'Transaction Description', mapping: 'dscr' },
            { name: 'Trans. Amount', mapping: 'amount', format: formatCurrency },
            { name: 'Accts Receivable', mapping: 'arCol', format: formatCurrency },
            { name: 'Accts Payable', mapping: 'apCol', format: formatCurrency },
        ];
    }

    return [
        { name: 'Client', mapping: 'clNo' },
        { name: 'Project', mapping: 'prNo' },
        { name: 'Lease No.', mapping: 'leaseNo' },
        { name: 'Lease Date', mapping: 'leaseDate', format: formatDateForDisplay },
        { name: 'Lessor', mapping: 'lessor' },
        { name: 'A/R Balance', mapping: 'arBalance', format: formatCurrency },
        { name: 'A/P Balance', mapping: 'apBalance', format: formatCurrency },
    ];
};

const downloadBlob = async (response: { blob: () => Promise<Blob> }, fileName: string): Promise<void> => {
    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(downloadUrl);
};

const LeaseSsrsReportDialog: React.FC<ILeaseSsrsReportDialogProps> = ({
    isOpen,
    reportType,
    onClose,
    initialLeaseNo = '',
    initialClientNo = '',
    initialProjectNo = '',
}) => {
    const { get, response } = useFetch();

    const [beginningLeaseNo, setBeginningLeaseNo] = useState('');
    const [endingLeaseNo, setEndingLeaseNo] = useState('');
    const [clientNo, setClientNo] = useState('');
    const [projectNo, setProjectNo] = useState('');
    const [leaseDate, setLeaseDate] = useState('');
    const [sortBy, setSortBy] = useState('2');
    const [rows, setRows] = useState<PagedResultSet<LeaseReportRow> | undefined>();
    const [pageNumber, setPageNumber] = useState(1);
    const [shouldReload, setShouldReload] = useState(false);
    const [message, setMessage] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [isQuerying, setIsQuerying] = useState(false);

    const isTransactionsReport = reportType === 'transactions';
    const title = isTransactionsReport ? 'Lease Transaction Report' : 'Lease Non-Zero Balance Report';
    const tableConfig: ITableConfig = {
        rowCount: PAGE_SIZE,
        pageNumber,
        idField: 'id',
        columns: getColumnsForReport(reportType),
    };

    useEffect(() => {
        if (!isOpen) {
            setMessage('');
            return;
        }

        setBeginningLeaseNo(initialLeaseNo || '');
        setEndingLeaseNo(initialLeaseNo || '');
        setClientNo(initialClientNo || '');
        setProjectNo(initialProjectNo || '');
        setLeaseDate('');
        setSortBy('2');
        setRows(undefined);
        setPageNumber(1);
        setShouldReload(true);
        setMessage('');
    }, [isOpen, initialClientNo, initialLeaseNo, initialProjectNo, reportType]);

    useEffect((): void | (() => void) => {
        if (!isOpen) {
            return undefined;
        }

        const originalOverflow = document.body.style.overflow;
        const originalPaddingRight = document.body.style.paddingRight;
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

        document.body.style.overflow = 'hidden';
        if (scrollbarWidth > 0) {
            document.body.style.paddingRight = `${scrollbarWidth}px`;
        }

        return () => {
            document.body.style.overflow = originalOverflow;
            document.body.style.paddingRight = originalPaddingRight;
        };
    }, [isOpen]);

    const buildSearchRequest = useCallback(
        (skip: number): { endpoint: string; params: URLSearchParams } => {
            const params = new URLSearchParams();
            let endpoint = '/v1.0/leases/admin/lease-transactions';

            params.append('skip', skip.toString());
            params.append('top', PAGE_SIZE.toString());

            if (isTransactionsReport) {
                if (beginningLeaseNo.trim()) {
                    params.append('beginningLeaseNo', beginningLeaseNo.trim());
                }

                if (endingLeaseNo.trim()) {
                    params.append('endingLeaseNo', endingLeaseNo.trim());
                }

                if (clientNo.trim()) {
                    params.append('clientNo', clientNo.trim());
                }

                if (projectNo.trim()) {
                    params.append('projectNo', projectNo.trim());
                }

                if (leaseDate) {
                    params.append('leaseDate', formatDateToMMDDYYYY(leaseDate));
                }
            } else {
                endpoint = '/v1.0/leases/admin/lease-non-zero-balance';
                if (clientNo.trim()) {
                    params.append('clientNo', clientNo.trim());
                }

                if (projectNo.trim()) {
                    params.append('projectNo', projectNo.trim());
                }

                params.append('sortBy', sortBy);
            }

            return { endpoint, params };
        },
        [beginningLeaseNo, clientNo, endingLeaseNo, isTransactionsReport, leaseDate, projectNo, sortBy]
    );

    const buildReportRequest = (): { endpoint: string; params: URLSearchParams; fileName: string } => {
        const params = new URLSearchParams();
        let endpoint = '/v1.0/leases/admin/lease-transactions/report';
        let fileName = 'LeaseTransactions.xlsx';

        if (isTransactionsReport) {
            params.append('beginningLeaseNo', beginningLeaseNo.trim());
            params.append('endingLeaseNo', endingLeaseNo.trim());
            params.append('clientNo', clientNo.trim());
            params.append('projectNo', projectNo.trim());
            params.append('leaseDate', formatDateToMMDDYYYY(leaseDate));
        } else {
            endpoint = '/v1.0/leases/admin/lease-non-zero-balance/report';
            fileName = 'LeaseNonZeroBalance.xlsx';
            params.append('clientNo', clientNo.trim());
            params.append('projectNo', projectNo.trim());
            params.append('sortBy', sortBy);
        }

        params.append('format', EXCEL_REPORT_FORMAT);

        return { endpoint, params, fileName };
    };

    const loadRows = useCallback(async (): Promise<void> => {
        setIsQuerying(true);
        setMessage('');

        try {
            const skip = (pageNumber - 1) * PAGE_SIZE;
            const { endpoint, params } = buildSearchRequest(skip);
            const data = await get(`${endpoint}?${params.toString()}`);

            if (!response.ok || !data) {
                setMessage(`Failed to query ${title}.`);
                setRows(undefined);
                return;
            }

            const items: LeaseReportRow[] = (data.items || []).map((row: LeaseReportRow, index: number) => ({
                ...row,
                id: `${reportType}-${row.leaseNo || ''}-${skip + index}`,
            }));

            const paged = new PagedResultSet<LeaseReportRow>();
            paged.items = items;
            paged.totalRecords = data.totalCount || 0;
            paged.pageNumber = pageNumber;
            paged.pageSize = PAGE_SIZE;
            setRows(paged);
        } catch {
            setMessage(`Error querying ${title}.`);
            setRows(undefined);
        } finally {
            setIsQuerying(false);
        }
    }, [buildSearchRequest, get, pageNumber, reportType, response, title]);

    useEffect(() => {
        if (!isOpen || !shouldReload) {
            return;
        }

        void loadRows();
        setShouldReload(false);
    }, [isOpen, shouldReload, loadRows]);

    useEffect(() => {
        let timeoutId: number | undefined;

        if (isOpen) {
            timeoutId = window.setTimeout(() => {
                setPageNumber(1);
                setShouldReload(true);
            }, 350);
        }

        return () => {
            if (timeoutId !== undefined) {
                window.clearTimeout(timeoutId);
            }
        };
    }, [isOpen, beginningLeaseNo, endingLeaseNo, clientNo, projectNo, leaseDate, sortBy, reportType]);

    const handleQuery = (): void => {
        setPageNumber(1);
        setShouldReload(true);
    };

    const handlePageChange = (newPage: number): void => {
        setPageNumber(newPage);
        setShouldReload(true);
    };

    const handleDownload = async (): Promise<void> => {
        setIsGenerating(true);
        setMessage('');

        try {
            const { endpoint, params, fileName } = buildReportRequest();
            await get(`${endpoint}?${params.toString()}`);

            if (!response.ok) {
                setMessage(`Failed to generate ${title}.`);
                return;
            }

            await downloadBlob(response, fileName);
        } catch {
            setMessage(`Error downloading ${title}.`);
        } finally {
            setIsGenerating(false);
        }
    };

    if (!isOpen) {
        return null;
    }

    return (
        <div style={dialogStyles.overlay} onClick={onClose}>
            <div style={dialogStyles.content} onClick={(e) => e.stopPropagation()}>
                <div style={dialogStyles.header}>
                    <h3 style={dialogStyles.title}>{title}</h3>
                    <button onClick={onClose} style={dialogStyles.closeButton} aria-label="Close report dialog">
                        <X style={{ width: '1rem', height: '1rem' }} />
                    </button>
                </div>

                <div style={dialogStyles.body}>
                    <div style={isTransactionsReport ? dialogStyles.transactionFieldsGrid : dialogStyles.balanceFieldsGrid}>
                        {isTransactionsReport ? (
                            <>
                                <Field
                                    label="Beginning Lease No."
                                    value={beginningLeaseNo}
                                    editable={true}
                                    onChange={(value) => setBeginningLeaseNo(value || '')}
                                />
                                <Field
                                    label="Ending Lease No."
                                    value={endingLeaseNo}
                                    editable={true}
                                    onChange={(value) => setEndingLeaseNo(value || '')}
                                />
                                <Field label="Client Number" value={clientNo} editable={true} onChange={(value) => setClientNo(value || '')} />
                                <Field label="Prospect Number" value={projectNo} editable={true} onChange={(value) => setProjectNo(value || '')} />
                                <Field
                                    label="Lease Date"
                                    type="date"
                                    value={leaseDate}
                                    editable={true}
                                    onChange={(value) => setLeaseDate(value || '')}
                                />
                            </>
                        ) : (
                            <>
                                <Field
                                    label="Client Number (Optional)"
                                    value={clientNo}
                                    editable={true}
                                    onChange={(value) => setClientNo(value || '')}
                                />
                                <Field label="Project Number" value={projectNo} editable={true} onChange={(value) => setProjectNo(value || '')} />
                                <SelectField
                                    label="Sort By"
                                    value={sortBy}
                                    editable={true}
                                    options={NON_ZERO_SORT_OPTIONS}
                                    onChange={(value) => setSortBy(value || '2')}
                                />
                            </>
                        )}
                    </div>

                    {message && <div style={dialogStyles.message}>{message}</div>}

                    <div style={dialogStyles.actionRow}>
                        <button onClick={handleQuery} style={dialogStyles.secondaryButton} disabled={isQuerying || isGenerating}>
                            <span style={dialogStyles.buttonInner}>
                                {isQuerying && <Loader2 className="animate-spin" style={{ width: '1rem', height: '1rem' }} />}
                                {isQuerying ? 'Querying...' : 'Query'}
                            </span>
                        </button>
                        <button onClick={handleDownload} style={dialogStyles.primaryButton} disabled={isGenerating || isQuerying}>
                            <span style={dialogStyles.buttonInner}>
                                {isGenerating && <Loader2 className="animate-spin" style={{ width: '1rem', height: '1rem' }} />}
                                {isGenerating ? 'Exporting...' : 'Export Excel'}
                            </span>
                        </button>
                    </div>

                    <div className="search-leases__table-wrapper lease-ssrs-report__table-wrapper" style={dialogStyles.tableSection}>
                        <Table isLoading={isQuerying} tableConfig={tableConfig} data={rows} enablePaging={true} onPageChange={handlePageChange} />
                    </div>
                </div>

                <div style={dialogStyles.footer}>
                    <button onClick={onClose} style={dialogStyles.secondaryButton} disabled={isGenerating || isQuerying}>
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

const dialogStyles: { [key: string]: React.CSSProperties } = {
    overlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.65)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 80,
        padding: '1rem',
    },
    content: {
        width: '95%',
        maxWidth: '1300px',
        backgroundColor: '#ffffff',
        borderRadius: '10px',
        border: '1px solid #E5E7EB',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.35)',
        display: 'flex',
        flexDirection: 'column',
        maxHeight: '90vh',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: '1px solid #E5E7EB',
        padding: '1rem 1.25rem',
        backgroundColor: '#F9FAFB',
    },
    title: {
        margin: 0,
        color: '#111827',
        fontSize: '1rem',
        fontWeight: 600,
    },
    closeButton: {
        color: '#6B7280',
        border: 'none',
        backgroundColor: 'transparent',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    body: {
        padding: '1rem 1.25rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.75rem',
        overflow: 'hidden',
        minHeight: 0,
    },
    transactionFieldsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
        gap: '0.75rem',
    },
    balanceFieldsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        gap: '0.75rem',
    },
    message: {
        border: '1px solid #F59E0B',
        backgroundColor: '#FFFBEB',
        color: '#92400E',
        borderRadius: '6px',
        padding: '0.55rem 0.75rem',
        fontSize: '0.85rem',
    },
    actionRow: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '0.75rem',
        flexWrap: 'wrap',
    },
    tableSection: {
        border: '1px solid #E5E7EB',
        borderRadius: '8px',
        overflowX: 'auto',
        overflowY: 'auto',
        maxHeight: '52vh',
        minHeight: '280px',
        backgroundColor: '#F9FAFB',
    },
    footer: {
        borderTop: '1px solid #E5E7EB',
        backgroundColor: '#F9FAFB',
        padding: '0.75rem 1.25rem',
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '0.75rem',
    },
    primaryButton: {
        border: 'none',
        backgroundColor: '#1E40AF',
        color: '#FFFFFF',
        borderRadius: '6px',
        padding: '0.55rem 1rem',
        fontWeight: 600,
        cursor: 'pointer',
    },
    secondaryButton: {
        border: '1px solid #D1D5DB',
        backgroundColor: '#FFFFFF',
        color: '#374151',
        borderRadius: '6px',
        padding: '0.55rem 1rem',
        fontWeight: 500,
        cursor: 'pointer',
    },
    buttonInner: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
    },
};

export default LeaseSsrsReportDialog;



// WEBPACK FOOTER //
// ./src/components/leases/sections/LeaseSsrsReportDialog.tsx